import React, { forwardRef, useEffect } from 'react'
import { useDebounce } from 'react-use'
import AutocompleteBase, {
  useAutocompleteInputState,
  TInputChangeHandler,
  TAutocompleteBaseChangeHandler,
  IAutocompleteBaseOption,
} from 'transfix-ui/components/AutocompleteBase'
import { isAutocompleteBaseOption } from 'transfix-ui/components/AutocompleteBase/utils/typeGuards'
import { useLoadsApi } from 'apps/fleet-planner/hooks'
import { ILoadFilters } from 'apps/fleet-planner/types'

const getComputedValue = (
  options: IAutocompleteBaseOption[],
  value: Pick<IAutocompleteBaseOption, 'id'> | null
): IAutocompleteBaseOption | null => {
  const { id: idProp } = value || {}
  const option = options.find(({ id }) => id === idProp)
  return option || null
}

export interface IAutocompleteLoadProps<
  V extends Pick<IAutocompleteBaseOption, 'id' | 'text'> = Pick<
    IAutocompleteBaseOption,
    'id' | 'text'
  >
> {
  disabled?: boolean
  label?: string
  onChange: (newVal: V | null) => void
  value: V | null
  required?: boolean
  inputError?: boolean
  inputValidationMessage?: string
  placeholder?: string
  pickupFacilityId?: string
  filters?: ILoadFilters
}

export const AutocompleteLoad = forwardRef<HTMLInputElement, IAutocompleteLoadProps>(
  (
    { disabled, filters = {}, label, onChange, pickupFacilityId, value: valueProp, ...props },
    ref
  ) => {
    const [inputValue, setInputValue] = useAutocompleteInputState('')
    const { getLoads, isLoading: isLoadsListLoading, loadsList } = useLoadsApi()

    /**
     * Refetch when the input value changes
     */
    useDebounce(
      () => {
        // Refetch even with empty input to show list of loads all the time
        getLoads({
          pageNumber: 1,
          pageSize: 10,
          filters: {
            ...filters,
            ...(inputValue ? { referenceNumber: inputValue } : {}),
            ...(pickupFacilityId ? { pickupFacilityId } : {}),
          },
        })
      },
      250,
      [inputValue, pickupFacilityId]
    )

    const changeHandler: TAutocompleteBaseChangeHandler = (_, newVal) => {
      if (isAutocompleteBaseOption(newVal)) {
        onChange(newVal)
      } else {
        onChange(null)
      }
    }

    const inputChangeHandler: TInputChangeHandler = (e, newVal) => {
      /**
       * MUI triggers an onChange event with `null` value when
       * trying to press delete/backspace when there's a value
       *
       * Because this is an invalid event, we ignore it
       */
      if (e) {
        setInputValue(e, newVal)
      }
    }

    /**
     * Set input value by default
     */
    useEffect(() => {
      if (!inputValue) {
        setInputValue({} as any, valueProp?.text || '')
      }
    }, [inputValue, setInputValue, valueProp?.text])

    // const filteredLoadsList = loadsList.filter(
    //   value => value.id && value.id.substring(0, 4) === 'TFX-'
    // )
    const options = loadsList.map(
      item =>
        ({
          id: item.id,
          text: item.externalId,
          groupIndex: 1,
        } as IAutocompleteBaseOption)
    )
    const value = getComputedValue(options, valueProp)

    return (
      <AutocompleteBase
        {...props}
        label={label || 'Load'}
        data-test='AutocompleteLoad'
        ref={ref}
        value={value}
        disabled={disabled}
        onChange={changeHandler}
        inputValue={inputValue}
        onInputChange={inputChangeHandler}
        options={options}
        loading={isLoadsListLoading}
        loadingText='Loading...'
        selectOnFocus
        autoSelect
        clearOnBlur={false}
      />
    )
  }
)
